/**
 * Google Analytics component with first-party cookie support
 * Compatible with Chrome's Privacy Sandbox
 */

import React, { useEffect, useState } from 'react';
import { GoogleAnalytics, initGoogleAnalytics } from '../../utils/analytics';

interface GoogleAnalyticsProps {
  measurementId: string;
  consentMode?: 'default' | 'granted' | 'denied';
  debugMode?: boolean;
  trackPageViews?: boolean;
}

const GoogleAnalyticsComponent: React.FC<GoogleAnalyticsProps> = ({
  measurementId,
  consentMode = 'default',
  debugMode = false,
  trackPageViews = true
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [ga, setGa] = useState<GoogleAnalytics | null>(null);

  useEffect(() => {
    // Load Google Analytics script
    const loadScript = () => {
      if (document.getElementById('ga-gtag-script')) {
        setIsLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.id = 'ga-gtag-script';
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      
      script.onload = () => {
        // Initialize dataLayer
        window.dataLayer = window.dataLayer || [];
        
        // Initialize gtag function
        window.gtag = function gtag() {
          window.dataLayer.push(arguments);
        };
        
        // Set initial config
        window.gtag('js', String(new Date().getTime()));
        
        setIsLoaded(true);
      };

      document.head.appendChild(script);
    };

    loadScript();
  }, [measurementId]);

  useEffect(() => {
    if (isLoaded && typeof window.gtag === 'function') {
      const gaInstance = initGoogleAnalytics({
        measurementId,
        consentMode,
        debugMode,
        sendPageView: trackPageViews
      });
      
      setGa(gaInstance);
    }
  }, [isLoaded, measurementId, consentMode, debugMode, trackPageViews]);

  useEffect(() => {
    if (ga && trackPageViews) {
      // Track page changes
      const handleRouteChange = () => {
        ga.trackPageView(window.location.pathname, document.title);
      };

      // Listen for navigation events
      window.addEventListener('popstate', handleRouteChange);
      
      // Override pushState and replaceState to catch navigation
      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;
      
      history.pushState = function(...args) {
        originalPushState.apply(history, args);
        setTimeout(handleRouteChange, 0);
      };
      
      history.replaceState = function(...args) {
        originalReplaceState.apply(history, args);
        setTimeout(handleRouteChange, 0);
      };

      return () => {
        window.removeEventListener('popstate', handleRouteChange);
        history.pushState = originalPushState;
        history.replaceState = originalReplaceState;
      };
    }
  }, [ga, trackPageViews]);

  // This component doesn't render anything
  return null;
};

export default GoogleAnalyticsComponent;
